/**
 * e-shot.trovex.com (Broadcast) staff auth — consumer of the shared auth
 * package (modules/auth via the @trovex/auth alias), mirroring
 * apps/users and apps/projects-portal. The bespoke SSO-redirect-only hook
 * this replaces lived here until phase 2; all behaviour now comes from
 * the package, this file is just config + the app's back-compat surface.
 *
 * Sign-in surface (identical to users/projects): inline 6-digit email
 * code (primary), with "Sign in with Trovex ID" as the fallback. No more
 * auto-redirect to id.trovex.com for signed-out visitors.
 *
 * IMPORTANT — no platform-access gate. Broadcast has never gated sign-in
 * on platformAccess/*: any @trovex.com account can sign in, and the app's
 * own AdminGate (eshot settings admins) protects everything that matters.
 * `disableAccessGate: true` preserves that exactly — even if a
 * platformAccess/eshot doc is created later, this app ignores it.
 */
import { useCallback, useMemo, useState } from 'react';
import { createAuthCore, deriveDisplayName } from '@trovex/auth/core';
import { useTrovexAuth } from '@trovex/auth/react';
import { auth, db, functions } from './firebase.js';

const APP_ID = 'eshot';

export function useAuth() {
  const core = useMemo(() => createAuthCore({
    auth,
    db,
    functions,
    appId: APP_ID,        // APP_CONFIGS key for the email-code callables
    platformId: APP_ID,   // required by the core; gate is disabled below
    storagePrefix: 'eshot',
  }), []);

  // The confirmed Trovex email for the session (user.email for code
  // sign-ins, the trovexEmail claim for SSO custom-token sign-ins). The
  // shared hook resolves it internally and hands it to onSignedIn.
  const [email, setEmail] = useState(null);
  const onSignedIn = useCallback((_user, resolvedEmail) => {
    setEmail(resolvedEmail || null);
  }, []);

  const shared = useTrovexAuth(core, { disableAccessGate: true, onSignedIn });
  const user = shared.user;

  return {
    ...shared,
    email: user ? (user.email || email || null) : null,
    displayName: user ? (user.displayName || deriveDisplayName(user.email || email || '')) : '',
    // Back-compat aliases for existing consumers (Layout, pages, hooks).
    signOut: shared.logout,
    signInWithSSORedirect: shared.loginWithTrovexId,
  };
}
